import React from 'react';

/**
 * Root-level React error boundary.
 *
 * Catches render errors anywhere in the component tree below this boundary
 * and shows a fallback UI instead of crashing the whole SPA. Previously a
 * single unhandled render error (e.g. in Orders.js timer logic, or a page
 * that threw on a nullable field) would take down the entire restaurant
 * dashboard and leave the owner staring at a blank white screen.
 *
 * NOTE: React error boundaries only catch errors during render,
 * lifecycle, and constructors of the children. They do NOT catch:
 *   - Event handlers (wrap those in try/catch yourself)
 *   - Async errors (handled by window.onunhandledrejection)
 *   - SSR errors (n/a here, CRA SPA)
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console and any wired-in crash reporter.
    // Keep this side-effect simple — a buggy crash reporter inside an
    // error boundary is a classic way to get into an infinite loop.
    // eslint-disable-next-line no-console
    console.error('🔥 [ErrorBoundary]', error, errorInfo?.componentStack);
  }

  componentDidMount() {
    // MED-3: hook into window-level unhandled async errors.
    // React error boundaries only catch errors from render/lifecycle/
    // ctor — promise rejections in useEffect/event handlers/socket
    // callbacks escape and white-screen the app. We latch onto them
    // here and flip into the same fallback UI as a render error.
    this._handleRejection = (e) => {
      const reason = e?.reason;

      // Benign background failures must NOT white-screen the app. Firebase Cloud
      // Messaging / Installations reject (e.g. "installations/app-offline",
      // "messaging/*") when the browser is offline, behind an ad-blocker/privacy
      // setting, or has no service worker. Push notifications are non-critical —
      // getFCMToken() already swallows these, but the Firebase SDK's internal
      // Installations request can reject on its own and escape as an unhandled
      // rejection. Log and ignore, same as the reCAPTCHA filter in _handleError.
      const code = (reason && reason.code) || '';
      const msg = (reason && reason.message) || String(reason || '');
      if (
        // Authoritative signal: Firebase sets `.code` to "installations/*" or
        // "messaging/*". Only fall back to the message for SDK errors that embed
        // the code in Firebase's own parenthesised format "(installations/…)" or
        // carry a distinctive FCM phrase — NOT a bare "installations/" substring,
        // which would wrongly swallow a real API error like "/api/installations/…".
        /^(installations|messaging)\//i.test(code) ||
        /app-offline|Could not process request\. Application offline|Failed to get FCM token|\((installations|messaging)\//i.test(msg)
      ) {
        if (typeof e?.preventDefault === 'function') e.preventDefault();
        // eslint-disable-next-line no-console
        console.warn('[ErrorBoundary] Ignored non-fatal Firebase messaging/installations rejection:', code || msg);
        return;
      }

      // eslint-disable-next-line no-console
      console.error('🔥 [ErrorBoundary] Unhandled rejection:', reason);
      // Only flip if we're not already in a failed state — a stray
      // rejection during the fallback render shouldn't re-trigger
      // and cause a loop.
      if (!this.state.hasError) {
        this.setState({
          hasError: true,
          error: reason instanceof Error ? reason : new Error(String(reason || 'Unhandled promise rejection'))
        });
      }
    };
    this._handleError = (e) => {
      // Ignore errors from third-party scripts (reCAPTCHA, analytics, etc.).
      // Google's reCAPTCHA cleanup fires a deferred setTimeout that tries to
      // access the challenge container's `.style` AFTER React unmounted the
      // login component. The error is harmless — auth already succeeded — but
      // without this filter it crashes the entire app via the fallback UI.
      //
      // Detection: the error's filename contains "recaptcha" or the stack
      // mentions it. Also ignore errors with no `.error` property (browser
      // noise like ResizeObserver or benign cross-origin script errors).
      const errorSource = e?.filename || e?.error?.stack || '';
      if (/recaptcha|gstatic\.com\/recaptcha/i.test(errorSource)) {
        // eslint-disable-next-line no-console
        console.warn('[ErrorBoundary] Ignored third-party reCAPTCHA error:', e?.error?.message || e?.message);
        return;
      }

      // eslint-disable-next-line no-console
      console.error('🔥 [ErrorBoundary] window.onerror:', e?.error || e?.message);
      // window.onerror also fires for synchronous runtime errors that
      // escape React (e.g. in setTimeout callbacks). Same handling.
      if (!this.state.hasError && e?.error) {
        this.setState({ hasError: true, error: e.error });
      }
    };
    window.addEventListener('unhandledrejection', this._handleRejection);
    window.addEventListener('error', this._handleError);
  }

  componentWillUnmount() {
    if (this._handleRejection) {
      window.removeEventListener('unhandledrejection', this._handleRejection);
    }
    if (this._handleError) {
      window.removeEventListener('error', this._handleError);
    }
  }

  handleReload = () => {
    // Full reload clears any stuck transient state.
    window.location.reload();
  };

  handleGoHome = () => {
    // Best-effort navigation home without a full reload.
    try {
      window.location.href = '/';
    } catch (_) {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F8FAFC',
            padding: '24px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: 16,
              padding: '40px 32px',
              maxWidth: 520,
              width: '100%',
              boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: 36,
              }}
              aria-hidden="true"
            >
              !
            </div>
            <h1
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: '#0F172A',
                marginBottom: 8,
              }}
            >
              Something went wrong
            </h1>
            <p
              style={{
                fontSize: 15,
                color: '#475569',
                lineHeight: 1.55,
                marginBottom: 24,
              }}
            >
              The page hit an unexpected error and had to stop. You can try
              reloading, or go back to the home screen. If this keeps
              happening, please contact support.
            </p>
            {process.env.NODE_ENV !== 'production' && this.state.error && (
              <pre
                style={{
                  textAlign: 'left',
                  fontSize: 12,
                  color: '#B91C1C',
                  backgroundColor: '#FEF2F2',
                  padding: 12,
                  borderRadius: 8,
                  overflow: 'auto',
                  maxHeight: 160,
                  marginBottom: 20,
                }}
              >
                {String(this.state.error?.message || this.state.error)}
              </pre>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                type="button"
                onClick={this.handleReload}
                style={{
                  padding: '12px 20px',
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#FFFFFF',
                  backgroundColor: '#EF4444',
                  border: 'none',
                  borderRadius: 10,
                  cursor: 'pointer',
                }}
              >
                Reload
              </button>
              <button
                type="button"
                onClick={this.handleGoHome}
                style={{
                  padding: '12px 20px',
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#EF4444',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #EF4444',
                  borderRadius: 10,
                  cursor: 'pointer',
                }}
              >
                Go home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
